/**
 * True when the natural-pixel crop covers the full source image (no real crop).
 */
export function isFullImageNaturalCrop(naturalCrop, naturalWidth, naturalHeight, tolerancePx = 2) {
  if (!naturalCrop || !naturalWidth || !naturalHeight) return false;
  return (
    (naturalCrop.x || 0) <= tolerancePx &&
    (naturalCrop.y || 0) <= tolerancePx &&
    (naturalCrop.width || 0) >= naturalWidth - tolerancePx &&
    (naturalCrop.height || 0) >= naturalHeight - tolerancePx
  );
}

/**
 * Convert a pixel crop from displayed image coordinates to natural (source) pixels.
 * react-image-crop's PixelCrop is relative to the rendered <img> size.
 */
export function scalePixelCropToNatural(
  pixelCrop,
  displayedWidth,
  displayedHeight,
  naturalWidth,
  naturalHeight
) {
  if (
    !pixelCrop ||
    !displayedWidth ||
    !displayedHeight ||
    !naturalWidth ||
    !naturalHeight
  ) {
    return null;
  }
  const sx = naturalWidth / displayedWidth;
  const sy = naturalHeight / displayedHeight;
  return {
    x: pixelCrop.x * sx,
    y: pixelCrop.y * sy,
    width: pixelCrop.width * sx,
    height: pixelCrop.height * sy,
  };
}

/**
 * Load an image for canvas operations (data URL or blob URL).
 */
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    // Needed for http(s) library URLs; harmless for blob:/data: sources
    if (typeof src === "string" && /^https?:\/\//i.test(src)) {
      image.crossOrigin = "anonymous";
    }
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (e) => reject(e));
    image.src = src;
  });
}

/**
 * Draw the cropped region from imageSrc into a canvas and return blob + data URL.
 */
export async function getCroppedImageData(
  imageSrc,
  pixelCrop,
  mimeType = "image/jpeg",
  quality = 0.92
) {
  const image = await loadImage(imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(pixelCrop.width));
  canvas.height = Math.max(1, Math.round(pixelCrop.height));
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Could not get canvas context");
  }
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    canvas.width,
    canvas.height
  );

  const skipQuality = mimeType === "image/png";
  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => {
        if (!b) reject(new Error("Crop produced empty image"));
        else resolve(b);
      },
      mimeType,
      skipQuality ? undefined : quality
    );
  });

  const dataUrl = skipQuality
    ? canvas.toDataURL(mimeType)
    : canvas.toDataURL(mimeType, quality);

  return { blob, dataUrl };
}
